/**
 * useOnlineStatus Hook
 *
 * Provides real-time online/offline status with network quality information.
 * Useful for showing offline indicators and disabling features when offline.
 */

import { useState, useEffect, useCallback } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  effectiveType?: '2g' | '3g' | '4g' | 'slow-2g';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export function useOnlineStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(() => ({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    ...getNetworkInfo(),
  }));

  const updateStatus = useCallback(() => {
    setStatus({
      isOnline: navigator.onLine,
      ...getNetworkInfo(),
    });
  }, []);

  useEffect(() => {
    // Initial check
    updateStatus();

    // Listen for online/offline events
    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);

    // Listen for network information changes if available
    const connection = getConnection();
    if (connection) {
      connection.addEventListener('change', updateStatus);
    }

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);

      if (connection) {
        connection.removeEventListener('change', updateStatus);
      }
    };
  }, [updateStatus]);

  return status;
}

/**
 * Get Network Information API connection
 */
function getConnection(): NetworkInformation | null {
  if ('connection' in navigator) {
    return (navigator as NavigatorWithConnection).connection;
  }
  return null;
}

/**
 * Get current network information
 */
function getNetworkInfo(): Partial<NetworkStatus> {
  const connection = getConnection();
  if (!connection) return {};

  return {
    effectiveType: connection.effectiveType as NetworkStatus['effectiveType'],
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
}

// Type definitions for Network Information API
interface NetworkInformation extends EventTarget {
  effectiveType: string;
  downlink: number;
  rtt: number;
  saveData: boolean;
}

interface NavigatorWithConnection extends Navigator {
  connection: NetworkInformation;
}

/**
 * Simple boolean hook for quick online checks
 */
export function useIsOnline(): boolean {
  const { isOnline } = useOnlineStatus();
  return isOnline;
}

/**
 * Hook to detect slow connections
 */
export function useIsSlowConnection(): boolean {
  const { effectiveType, saveData } = useOnlineStatus();

  if (saveData) return true;
  if (effectiveType === '2g' || effectiveType === 'slow-2g') return true;

  return false;
}
